import { useContext, useEffect, useState, useMemo } from 'react'
import { useParams, useNavigate } from 'react-router-dom'

import { AppContext } from '/providers/app-context'

import PageWrapper from '/components/page-wrapper'
import HelpText from '/help/template-wizard'
import Menu from '/components/templates/template-nav-menu'

import { getTemplateData, getPassTypeData } from '/providers/templates-api'

import Stepper from '/components/wizard/stepper'
import Preview from '/components/wizard/common/wizard-preview'
import Footer from '/components/wizard/footer'

import wizard_templates from '/components/wizard/config/wizard-templates'
import steps from '/components/wizard/config/steps'
import { enqueueSnackbar } from 'notistack'

const TemplateWizard = () => {

  const { templateId } = useParams()
  const { apiSource, apiKeys, templates } = useContext(AppContext)
  const navigate = useNavigate()

  const template = useMemo(() => { return templates.filter(t => t.id === templateId)[0] }, [templates, templateId])

  const [data, setData] = useState(null)

  useEffect(() => {
    initialise()
  }, [templateId])

  const initialise = async () => {
    const template_data = await getTemplateData({ apiSource, apiKeys, templateId })

    if (template_data.error) {
      navigate("/templates")
    }

    if (template_data.template.wizardPermitted === 0) {
      navigate("../edit")
    }
    setData(template_data.template)
  }

  if (data) {
    return (
      <PageWrapper title="Template Wizard" HelpText={HelpText} heading={`Template Wizard : ${template?.name}`} menu={<Menu template={template} />}>
        <Content template={template} data={data} />
      </PageWrapper>
    )
  }
}

export default TemplateWizard

const Content = ({ template, data }) => {

  const { apiSource, apiKeys, client } = useContext(AppContext)

  const [activeStep, setActiveStep] = useState(0)
  const [prevButtonState, setPrevButtonState] = useState("hidden")
  const [nextButtonState, setNextButtonState] = useState("disabled")
  const [passTypeData, setPassTypeData] = useState()

  const [appleModel, setAppleModel] = useState()
  const [appleTemplate, setAppleTemplate] = useState()
  const [objectData, setObjectData] = useState()
  const [classData, setClassData] = useState()
  const [instanceData, setInstanceData] = useState()
  const [scannerData, setScannerData] = useState()
  const [scannerInstanceData, setScannerInstanceData] = useState()
  const [applePassType, setApplePassType] = useState()
  const [appleSubType, setAppleSubType] = useState()
  const [googlePassType, setGooglePassType] = useState()
  const [googleSubType, setGoogleSubType] = useState()
  const [disableExpiredGooglePasses, setDisableExpiredGooglePasses] = useState(false)
  const [wizardState, setWizardState] = useState({})
  const [passTypeId, sePassTypeId] = useState("")

  const [originalModel,setOriginalModel] = useState()
  const [originalTemplate,setOriginalTemplate] = useState()
  const [originalClassData,setOriginalClassData] = useState()
  const [originalObjectData,setOriginalObjectData] = useState()
  const [originalScannerData,setOriginalScannerData] = useState()

  const getPassTypeInfo = async passType => {
    const ptd = await getPassTypeData({ apiSource, apiKeys, passType })
    if (ptd.ok) {
      setPassTypeData(ptd.data)
    } else {
      enqueueSnackbar({message:PrintDisabled.error,variant:"error"})
    }
  }

  useEffect(() => {
    setAppleModel(data.apple_model)
    setAppleTemplate(data.apple_template)
    setClassData(data.google_class)
    setObjectData(data.google_object)
    setInstanceData(data.instance_data)
    setScannerData(data.scanner_data)
    setScannerInstanceData(data.scanner_instance_data)
    setApplePassType(data.apple_pass_type)
    setAppleSubType(data.apple_sub_type)
    setGooglePassType(data.google_pass_type)
    setGoogleSubType(data.google_sub_type)
    sePassTypeId(data.pass_type_id)
    setOriginalModel(data.apple_model)
    setOriginalScannerData(data.scanner_data)
    setOriginalTemplate(data.apple_template)
    setOriginalClassData(data.google_class)
    setOriginalObjectData(data.google_object)
    getPassTypeInfo(data.pass_type_id)
  }, [data])

  const basePassType = useMemo(() => {
    if (template) {
      return steps[template.pass_type_id] ? template.pass_type_id : "standard" // Return type if it is defined in steps, otherwise use the "standard" type
    }
    // Default to none if we do not have a template yet
    return "none"
  }, [template])

  const ActiveTemplate = useMemo(() => basePassType ? wizard_templates[basePassType][steps[basePassType][activeStep]] : "none", [activeStep, basePassType])

  
  const setGoogleBoth = ({googleClass,googleObject}) => {
    setClassData(googleClass)
    setObjectData(googleObject)
  }


  if (template && ActiveTemplate) return (
    <>
      <Stepper basePassType={basePassType} steps={steps} activeStep={activeStep} setActiveStep={setActiveStep} />

      <div className="flex flex-row gap-10 w-full p-5 h-full">
        <div className="flex-grow overflow-y-auto h-full">
          <ActiveTemplate
            clientId={client?.id}
            client={client}
            templateId={template.id}
            applePassType={applePassType}
            appleSubType={appleSubType}
            googlePassType={googlePassType}
            smartixPassType={template.pass_type_id}
            basePassType={basePassType}
            setPrevButtonState={setPrevButtonState}
            setNextButtonState={setNextButtonState}
            passTypeData={passTypeData}
            template={appleTemplate}
            objectData={objectData}
            classData={classData}
            model={appleModel}
            instanceData={instanceData}
            setInstanceData={setInstanceData}
            scannerData={scannerData}
            setScannerData={setScannerData}
            scannerInstanceData={scannerInstanceData}
            setScannerInstanceData={setScannerInstanceData}
            setTemplate={setAppleTemplate}
            setModel={setAppleModel}
            setClassData={setClassData}
            setObjectData={setObjectData}
            setGoogleBoth={setGoogleBoth}
            wizardState={wizardState}
            setWizardState={setWizardState}
            disableExpiredGooglePasses={disableExpiredGooglePasses}
            setDisableExpiredGooglePasses={setDisableExpiredGooglePasses}
            passTypeId={passTypeId}
          />
          <Footer apiSource={apiSource} 
            apiKeys={apiKeys} 
            prevButtonState={prevButtonState} 
            nextButtonState={nextButtonState} 
            activeStep={activeStep} 
            setActiveStep={setActiveStep} 
            clientId={client?.id} 
            templateId={template.id}
            appleModel={appleModel}
            appleTemplate={appleTemplate}
            objectData={objectData}
            classData={classData}
            originalModel={originalModel}
            scannerData={scannerData}
            setScannerData={setScannerData}
            originalScannerData={originalScannerData}
            setOriginalScannerData={setOriginalScannerData}
            setOriginalModel={setOriginalModel}
            originalTemplate={originalTemplate}
            setOriginalTemplate={setOriginalTemplate}
            originalClassData={originalClassData}
            setOriginalClassData={setOriginalClassData}
            originalObjectData={originalObjectData}
            setOriginalObjectData={setOriginalObjectData}/>

          <div style={{ height: 200, width: 500 }} />
        </div>
        <div className='h-full' style={{ width: 800 }}>
          <Preview
            clientId={client?.id}
            smartixPassType={template.pass_type_id}
            applePassType={applePassType}
            appleSubType={appleSubType}
            googlePassType={googlePassType}
            googleSubType={googleSubType}
            model={appleModel}
            classData={classData}
            objectData={objectData}
            template={appleTemplate}
            instanceData={instanceData}
            scannerInstanceData={scannerInstanceData}
            templateId={template.id}
          />
        </div>
      </div>
    </>
  )
}